import { useMemo, useState } from "react"
import { GetApp } from "../../App"
import { logger } from "../../App/Logger"
import { isUserAdviceMonitor, UserAdviceInstance, UserAdviceMonitor, UserAdviceState } from "../../Views/UserAdvice/Advice"
import { classNames } from "../../Components"
import { Icon, Icons } from "../Icon"
import { Menu, MenuItemDividerProps, MenuItemProps } from "../Menu"
import { IAppState } from "../../Types"

interface UserAdviceMonitorTogglesProps {
  // The monitor-role advices at `UserAdviceLocation.toolbar`.
  advices: UserAdviceInstance[];
  app: IAppState;
  // Retained for callers that render separate responsive header branches; both
  // branches now intentionally use the same menu.
  collapsed?: boolean;
  // Renders the menu already open -- for catalog/demo fixtures and
  // tests only, mirroring `Menu`'s own `initOpen`.
  initOpen?: boolean;
  state: UserAdviceState;
}

// The ship toolbar's Alerts menu: anchor, depth and navigation lights today,
// extensible to future monitor-role alert settings.
export function UserAdviceMonitorToggles({ advices, app, initOpen, state }: UserAdviceMonitorTogglesProps) {
  const monitors = useMemo(() => advices.filter((advice) => (
    isUserAdviceMonitor(advice.parent)
  )), [advices]);

  if (monitors.length == 0) {
    return null;
  }
  return <UserAdviceMonitorMenu advices={monitors} app={app} initOpen={initOpen} state={state} />;
}

// `monitors` above established the parent implements the contract.
function monitorOf(advice: UserAdviceInstance): UserAdviceMonitor {
  return advice.parent as unknown as UserAdviceMonitor;
}

async function switchMonitor(advice: UserAdviceInstance, app: IAppState) {
  const monitor = monitorOf(advice);
  try {
    if (monitor.isActive()) {
      await monitor.deactivate(app);
    } else {
      await monitor.activate(app);
    }
    return true;
  } catch (err) {
    logger.debug("UserAdviceMonitorToggle update", err);
    return false;
  }
}

function UserAdviceMonitorMenu({ advices, app, initOpen, state }: { advices: UserAdviceInstance[]; app: IAppState; initOpen?: boolean; state: UserAdviceState }) {
  const [revision, setRevision] = useState(0);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const watchActive = advices.some((advice) => (
    (advice.props.type == "anchor" || advice.props.type == "depth")
    && monitorOf(advice).isActive()
  ));
  const watchProblem = advices.some((advice) => {
    const monitor = monitorOf(advice);
    return monitor.isActive() && monitor.hasProblem?.(app);
  });
  const t = GetApp().getT("MenuAlerts");

  const items: MenuItemProps[] = useMemo(() => {
    const result: MenuItemProps[] = [];
    advices.forEach((advice) => {
      // Add divider before navigation lights
      if (advice.props.type == "lights") {
        result.push(MenuItemDividerProps);
      }
      const active = monitorOf(advice).isActive();
      const updating = pending.has(advice.id);
      result.push({
        className: ["UserAdviceMonitorMenu-item", "UserAdviceMonitorMenu-settings"],
        icon: advice.props.icon,
        id: advice.id,
        title: advice.props.title,
        onClick: () => {
          state.setSelected(advice.id);
          return true;
        },
        render: () => (
          <button
            aria-label={advice.props.title}
            aria-pressed={active}
            className={classNames(["UserAdviceMonitorMenu-toggle", active && "active"])}
            disabled={updating}
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setPending((value) => new Set(value).add(advice.id));
              void switchMonitor(advice, app)
                .then((changed) => {
                  if (changed) {
                    setRevision((value) => value + 1);
                  }
                })
                .finally(() => {
                  setPending((value) => {
                    const next = new Set(value);
                    next.delete(advice.id);
                    return next;
                  });
                });
            }}
          >
            <span className="button-style">
              <Icon icon={active ? Icons.On1 : Icons.Off1} className="toggle-button-left" />
              <Icon icon={active ? Icons.On2 : Icons.Off2} className="toggle-button-right" />
            </span>
          </button>
        ),
      });
    });
    return result;
  }, [advices, app, pending, revision, state]);

  return (
    <div className="UserAdviceMonitorMenu">
      <Menu
        button={<><Icon icon={Icons.Alert} /> {t("title")}</>}
        buttonClass={classNames(["UserAdviceMonitorMenu-button", "button", watchActive && "active", watchProblem && "error"])}
        global={true}
        initOpen={initOpen}
        items={items}
      />
    </div>
  );
}
